// src/pages/Trend/components/analysis/sustainability/CertificationChart.js
import React, { memo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import './CertificationChart.css';

// Render filter label with monospace number for alignment
const renderFilterLabel = (label) => {
  const match = label.match(/^(\d+)(.*)$/);
  if (match) {
    return (
      <>
        <span className="filter-label-number">{match[1]}</span>
        {match[2]}
      </>
    );
  }
  return label;
};

ChartJS.register(ArcElement, Tooltip, Legend);

// Certificate specific colors
const CERT_COLORS = [
  '#1380A1',  // BBC blue
  '#FAAB18',  // BBC yellow
  '#D55E00',  // BBC orange
  '#6C8EA0',  // Muted blue-gray
  '#A8C4D4'   // Light blue
];

// Narrative component following design guide
const CertificationNarrative = ({ primaryData, secondaryData, hasSecondary, translate }) => {
  const primaryTotal = primaryData?.total_certificates || 0;
  const primaryTop = primaryData?.certificate_distribution?.label?.[0] || '';
  const primaryTypes = primaryData?.certificate_distribution?.label?.length || 0;

  const getLevel = (total) => {
    if (total >= 100) return { level: translate('trendPage.sustainability.certification.levels.high'), color: BBC_STYLE.POSITIVE_COLOR };
    if (total >= 50) return { level: translate('trendPage.sustainability.certification.levels.medium'), color: BBC_STYLE.SECONDARY_COLOR };
    return { level: translate('trendPage.sustainability.certification.levels.low'), color: BBC_STYLE.NEGATIVE_COLOR };
  };

  if (!hasSecondary) {
    const { level, color } = getLevel(primaryTotal);
    return (
      <div className="certification-narrative">
        <h2 className="narrative-title">
          <span className="highlight-level" style={{ color, borderBottom: `2px solid ${color}` }}>
            {level}
          </span>
          {' '}{translate('trendPage.sustainability.certification.activity')}
        </h2>
        <div className="narrative-subtitle">
          <span className="stat-pill" style={{ background: `${BBC_STYLE.PRIMARY_COLOR}15` }}>
            {translate('trendPage.sustainability.certification.total')}: {primaryTotal}{translate('trendPage.sustainability.certificateUnit')}
          </span>
          <span className="stat-divider">•</span>
          <span>{primaryTypes}{translate('trendPage.sustainability.certification.types')}</span>
          <span className="stat-divider">•</span>
          <span>{translate('trendPage.sustainability.certification.main')}: <strong>{primaryTop}</strong></span>
        </div>
      </div>
    );
  }

  // Comparison mode
  const secondaryTotal = secondaryData?.total_certificates || 0;
  const diff = Math.abs(primaryTotal - secondaryTotal);
  const similar = diff <= 10;
  const higherSet = primaryTotal > secondaryTotal ? translate('trendPage.filters.set1') : translate('trendPage.filters.set2');
  const higherColor = primaryTotal > secondaryTotal ? BBC_STYLE.PRIMARY_COLOR : BBC_STYLE.SECONDARY_COLOR;

  return (
    <div className="certification-narrative">
      <h2 className="narrative-title">
        {similar ? (
          <>
            {translate('trendPage.sustainability.narrative.bothFilters')}{' '}
            <span className="highlight-level" style={{ color: BBC_STYLE.HIGHLIGHT_COLOR, borderBottom: `2px solid ${BBC_STYLE.HIGHLIGHT_COLOR}` }}>
              {translate('trendPage.sustainability.narrative.similar')}
            </span>
            {' '}{translate('trendPage.sustainability.certification.level')}
          </>
        ) : (
          <>
            <span className="highlight-level" style={{ color: higherColor, borderBottom: `2px solid ${higherColor}` }}>
              {higherSet}
            </span>
            {' '}{translate('trendPage.sustainability.certification.moreCertifications', { diff })}
          </>
        )}
      </h2>
      <div className="narrative-comparison">
        <div className="comparison-item">
          <div className="comparison-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
          <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set1'))}:</span>
          <span className="comparison-value" style={{ color: BBC_STYLE.PRIMARY_COLOR }}>
            {primaryTotal}{translate('trendPage.sustainability.certificateUnit')}
          </span>
        </div>
        <div className="comparison-item">
          <div className="comparison-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
          <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set2'))}:</span>
          <span className="comparison-value" style={{ color: BBC_STYLE.SECONDARY_COLOR }}>
            {secondaryTotal}{translate('trendPage.sustainability.certificateUnit')}
          </span>
        </div>
      </div>
    </div>
  );
};

const CertificationChart = memo(({ data }) => {
  const { translate } = useLanguage();

  if (!data?.primary?.certificate_metrics) {
    return null;
  }

  const hasSecondaryData = data.secondary && data.secondary.certificate_metrics;
  const primaryData = data.primary.certificate_metrics;
  const secondaryData = hasSecondaryData ? data.secondary.certificate_metrics : null;

  const getChartData = (certData, isSecondary = false) => {
    if (!certData) return null;
    const colors = isSecondary
      ? CERT_COLORS.map((color, i) => i === 0 ? BBC_STYLE.SECONDARY_COLOR : color)
      : CERT_COLORS;

    return {
      labels: certData.certificate_distribution.label,
      datasets: [{
        data: certData.certificate_distribution.values,
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 6,
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'white',
        titleColor: '#333',
        bodyColor: '#333',
        bodyFont: { size: 11 },
        titleFont: { size: 12, weight: 'bold' },
        padding: 10,
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 6,
        callbacks: {
          label: (context) => {
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    cutout: '65%',
    animation: { duration: 600 }
  };

  const renderLegend = (certData, isSecondary = false) => {
    if (!certData) return null;
    const colors = isSecondary
      ? CERT_COLORS.map((color, i) => i === 0 ? BBC_STYLE.SECONDARY_COLOR : color)
      : CERT_COLORS;
    const total = certData.total_certificates;

    return (
      <div className="cert-legend">
        {certData.certificate_distribution.label.map((label, idx) => {
          const value = certData.certificate_distribution.values[idx];
          const percent = total > 0 ? Math.round((value / total) * 100) : 0;
          return (
            <div key={idx} className="cert-legend-item">
              <span className="cert-legend-dot" style={{ background: colors[idx] }} />
              <span className="cert-legend-label">{label}</span>
              <span className="cert-legend-value" style={{ color: colors[idx] }}>{value}</span>
              <span className="cert-legend-percent">({percent}%)</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="certification-wrapper">
      {/* Narrative Section */}
      <CertificationNarrative
        primaryData={primaryData}
        secondaryData={secondaryData}
        hasSecondary={hasSecondaryData}
        translate={translate}
      />

      {/* Main Content */}
      <div className={`certification-main-content ${hasSecondaryData ? 'has-comparison' : ''}`}>
        {hasSecondaryData ? (
          // Comparison mode: two charts side by side
          <>
            <div className="cert-chart-panel">
              <div className="cert-chart-header">
                <span className="cert-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
                <span className="cert-label">{renderFilterLabel(translate('trendPage.filters.set1'))}</span>
              </div>
              <div className="cert-chart-area">
                <Doughnut data={getChartData(primaryData)} options={chartOptions} />
                <div className="cert-center-label">
                  <span className="cert-total" style={{ color: BBC_STYLE.PRIMARY_COLOR }}>{primaryData.total_certificates}</span>
                  <span className="cert-total-label">{translate('trendPage.sustainability.totalCount')}</span>
                </div>
              </div>
              {renderLegend(primaryData)}
            </div>
            <div className="cert-chart-panel">
              <div className="cert-chart-header">
                <span className="cert-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
                <span className="cert-label">{renderFilterLabel(translate('trendPage.filters.set2'))}</span>
              </div>
              <div className="cert-chart-area">
                <Doughnut data={getChartData(secondaryData, true)} options={chartOptions} />
                <div className="cert-center-label">
                  <span className="cert-total" style={{ color: BBC_STYLE.SECONDARY_COLOR }}>{secondaryData.total_certificates}</span>
                  <span className="cert-total-label">{translate('trendPage.sustainability.totalCount')}</span>
                </div>
              </div>
              {renderLegend(secondaryData, true)}
            </div>
          </>
        ) : (
          // Single mode: chart with legend on side
          <>
            <div className="cert-chart-single">
              <Doughnut data={getChartData(primaryData)} options={chartOptions} />
              <div className="cert-center-label">
                <span className="cert-total" style={{ color: BBC_STYLE.PRIMARY_COLOR }}>{primaryData.total_certificates}</span>
                <span className="cert-total-label">{translate('trendPage.sustainability.totalCount')}</span>
              </div>
            </div>
            {renderLegend(primaryData)}
          </>
        )}
      </div>
    </div>
  );
});

export default CertificationChart;
